import { ArrowUpRight, Play, Maximize2, Volume2, VolumeX, X } from "lucide-react";
import { useRef, useState, useEffect } from "react";

const projects = [
  {
    id: "froid-agroalimentaire",
    sector: "Industrie agroalimentaire",
    title: "Refonte complète de la production de froid",
    image: "https://images.unsplash.com/photo-1716643863806-989dd76ae093?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBpbmR1c3RyaWFsJTIwZmFjaWxpdHklMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODI3OTA2NDR8MA&ixlib=rb-4.1.0&q=85",
    finance: "1,84 M€",
    savings: "38 %",
    payback: "2,1 ans",
    desc: "Remplacement de 4 groupes froids HFC par cascade CO₂ transcritique, récupération de chaleur HT vers procédé.",
  },
  {
    id: "hotellerie-cdc",
    sector: "Groupe hôtelier",
    title: "Coup de Pouce Chaufferie sur 11 établissements",
    image: "https://images.unsplash.com/photo-1611892440504-42a792e24d32?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80",
    finance: "612 k€",
    savings: "29 %",
    payback: "1,4 an",
    desc: "Substitution chaudières fioul → PAC haute température + isolation des réseaux, déploiement coordonné en 9 mois.",
  },
  {
    id: "logistique-led",
    sector: "Plateforme logistique",
    title: "Relamping LED + déstratification d'air",
    image: "https://images.unsplash.com/photo-1553413077-190dd305871c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80",
    finance: "284 k€",
    savings: "44 %",
    payback: "11 mois",
    desc: "42 000 m² entièrement réinstrumentés. Déstratification permettant de baisser la consigne de chauffage de 2°C.",
  },
  {
    id: "coproprio-pac",
    sector: "Copropriété 184 lots",
    title: "Pompe à chaleur collective + isolation thermique",
    image: "https://images.pexels.com/photos/2119714/pexels-photo-2119714.jpeg",
    finance: "417 k€",
    savings: "31 %",
    payback: "Reste à charge < 5 %",
    desc: "PAC eau/eau géothermale en sous-sol, ITE complète, ventilation hygroréglable B. Conduite de travaux 7 mois.",
  },
  {
    id: "datacenter-freecooling",
    sector: "Data center",
    title: "Free-cooling et récupération sur baies",
    image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80",
    finance: "1,12 M€",
    savings: "26 %",
    payback: "2,7 ans",
    desc: "Optimisation aéraulique des salles, free-cooling indirect 9 mois/12, récupération vers bâtiment tertiaire mitoyen.",
  },
  {
    id: "papeterie-vapeur",
    sector: "Papeterie industrielle",
    title: "Réseau vapeur, calorifugeage et purgeurs",
    image: "https://images.pexels.com/photos/2760241/pexels-photo-2760241.jpeg",
    finance: "238 k€",
    savings: "22 %",
    payback: "8 mois",
    desc: "Audit réseau vapeur (1,9 km), remplacement de 86 purgeurs, calorifugeage complet, monitoring continu.",
  },
];

function ShowreelVideo() {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onFsChange = () => {
      const active = !!document.fullscreenElement;
      setIsFullscreen(active);
      if (videoRef.current) {
        videoRef.current.muted = !active;
        setMuted(!active);
      }
    };
    document.addEventListener("fullscreenchange", onFsChange);

    const onKey = (e) => { if (e.key === "Escape" && isFullscreen) setIsFullscreen(false); };
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("keydown", onKey);
    };
  }, [isFullscreen]);

  const toggleSound = (e) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setMuted(videoRef.current.muted);
  };
  const togglePlay = (e) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    if (videoRef.current.paused) { videoRef.current.play(); setPlaying(true); }
    else { videoRef.current.pause(); setPlaying(false); }
  };

  // Try native Fullscreen API first; fall back to CSS-based fullscreen
  // (necessary because sandboxed iframes / permission policies block requestFullscreen).
  const enterFullscreen = () => {
    const el = containerRef.current;
    if (isFullscreen) {
      if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
      setIsFullscreen(false);
      if (videoRef.current) { videoRef.current.muted = true; setMuted(true); }
      return;
    }
    // Attempt native FS with graceful fallback
    const tryNative = () => {
      if (el?.requestFullscreen) return el.requestFullscreen();
      if (el?.webkitRequestFullscreen) return el.webkitRequestFullscreen();
      if (videoRef.current?.webkitEnterFullscreen) { videoRef.current.webkitEnterFullscreen(); return Promise.resolve(); }
      return Promise.reject(new Error("no api"));
    };
    Promise.resolve()
      .then(() => tryNative())
      .catch(() => {
        // CSS-fullscreen fallback (works inside sandboxed iframes)
        setIsFullscreen(true);
        if (videoRef.current) {
          videoRef.current.muted = false;
          setMuted(false);
          videoRef.current.play().catch(() => {});
        }
      });
  };

  const exitCssFullscreen = (e) => {
    e.stopPropagation();
    setIsFullscreen(false);
    if (videoRef.current) { videoRef.current.muted = true; setMuted(true); }
  };

  return (
    <div data-testid="realisations-showreel" className="relative mb-20 md:mb-24 rounded-3xl overflow-hidden bg-[#0F172A] shadow-[0_60px_120px_-40px_rgba(15,23,42,0.55)]">
      <div
        ref={containerRef}
        onClick={enterFullscreen}
        className={
          isFullscreen
            ? "fixed inset-0 z-[9999] bg-black cursor-zoom-out group/showreel flex items-center justify-center"
            : "relative aspect-[16/9] md:aspect-[21/9] cursor-zoom-in group/showreel"
        }
      >
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=1920&q=80"
          className={
            isFullscreen
              ? "max-h-screen max-w-full w-auto h-auto object-contain"
              : "absolute inset-0 h-full w-full object-cover"
          }
        >
          <source src="/videos/realisations.webm?v=8" type="video/webm" />
          <source src="/videos/realisations.mp4?v=8" type="video/mp4" />
        </video>

        {/* Close button in CSS-fullscreen */}
        {isFullscreen && (
          <button
            onClick={exitCssFullscreen}
            data-testid="showreel-close-fs-btn"
            aria-label="Quitter le plein écran"
            className="fixed top-5 right-5 z-[10000] inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/25 backdrop-blur-md border border-white/25 px-4 py-2.5 text-white text-xs font-semibold tracking-wide uppercase transition"
          >
            <X className="h-4 w-4" /> Quitter
          </button>
        )}

        {/* Gradient overlay - subtle bottom fade only to keep buttons visible */}
        {!isFullscreen && (
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0F172A]/70 to-transparent pointer-events-none" />
        )}

        {/* Fullscreen hint (top-left) */}
        {!isFullscreen && (
          <div className="absolute top-6 left-6 inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/15 px-3 py-1.5 text-white/85 text-[10px] font-semibold tracking-[0.2em] uppercase opacity-0 group-hover/showreel:opacity-100 transition-opacity pointer-events-none">
            <Maximize2 className="h-3 w-3" />
            Cliquer pour plein écran
          </div>
        )}

        {/* Playback controls - bottom-left, minimal to not compete with burned subtitles */}
        {!isFullscreen && (
          <div className="absolute bottom-5 left-5 sm:bottom-7 sm:left-7 flex flex-wrap items-center gap-2">
            <button
              onClick={togglePlay}
              data-testid="showreel-play-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 px-4 py-2.5 text-white text-xs font-semibold tracking-wide uppercase transition"
            >
              <Play className="h-3.5 w-3.5" fill="currentColor" />
              {playing ? "Pause" : "Lecture"}
            </button>
            <button
              onClick={toggleSound}
              data-testid="showreel-sound-btn"
              className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 px-4 py-2.5 text-white text-xs font-semibold tracking-wide uppercase transition"
            >
              {muted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
              {muted ? "Activer le son" : "Couper le son"}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); enterFullscreen(); }}
              data-testid="showreel-fullscreen-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#10B981] hover:bg-[#0EA372] px-4 py-2.5 text-white text-xs font-semibold tracking-wide uppercase transition shadow-lg shadow-[#10B981]/30"
            >
              <Maximize2 className="h-3.5 w-3.5" />
              Plein écran
            </button>
          </div>
        )}

        {/* Corner metric badge - top-right */}
        {!isFullscreen && (
          <div className="hidden md:flex absolute top-6 right-6 flex-col items-end rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 px-5 py-4 pointer-events-none">
            <div className="text-[10px] uppercase tracking-[0.28em] text-white/70">Chantiers 2025</div>
            <div className="font-display text-3xl font-bold text-white leading-none mt-1">+240</div>
            <div className="text-[11px] text-white/70 mt-1">sites industriels &amp; tertiaires</div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function RealisationsGallery({ limit }) {
  const items = limit ? projects.slice(0, limit) : projects;
  return (
    <section data-testid="realisations-gallery" className="bg-gradient-to-b from-[#F8FAFC] to-white border-y border-black/5">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-24 md:py-32">
        <ShowreelVideo />

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="text-[11px] font-semibold tracking-[0.28em] text-[#10B981] uppercase mb-5">Réalisations</div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] tracking-tight text-balance leading-[1.05]">
              Des projets concrets, des économies vérifiées.
            </h2>
          </div>
          <p className="md:max-w-md text-[#475569] leading-relaxed">
            Un échantillon de programmes financés CEE par ECONATION ces 24 derniers mois,
            tous secteurs confondus.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((p) => (
            <article
              key={p.id}
              data-testid={`realisation-${p.id}`}
              className="group relative bg-white rounded-2xl overflow-hidden border border-black/[0.06] hover:border-[#10B981]/40 hover:shadow-[0_30px_80px_-40px_rgba(15,23,42,0.3)] transition-all"
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-[#0F172A]">
                <img src={p.image} alt={p.title} className="h-full w-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-[1.2s]" />
                <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-white/85 backdrop-blur text-[11px] font-semibold uppercase tracking-wider text-[#0F172A]">
                  {p.sector}
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-display text-lg font-semibold text-[#0F172A] mb-3 leading-snug">{p.title}</h3>
                <p className="text-sm text-[#475569] leading-relaxed mb-5">{p.desc}</p>
                <div className="grid grid-cols-3 gap-2 pt-4 border-t border-black/[0.06]">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] mb-1">Financé</div>
                    <div className="font-display font-semibold text-[#10B981]">{p.finance}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] mb-1">Économies</div>
                    <div className="font-display font-semibold text-[#0F172A]">{p.savings}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] mb-1">Payback</div>
                    <div className="font-display font-semibold text-[#0F172A]">{p.payback}</div>
                  </div>
                </div>
              </div>
              <div className="absolute top-4 right-4 h-9 w-9 rounded-full bg-[#0F172A] text-white grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity">
                <ArrowUpRight className="h-4 w-4" />
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
